import { HttpErrorResponse, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { GridDataResult } from '@progress/kendo-angular-grid';
import { BehaviorSubject, Observable } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';
import { IdEntity } from '../models/id-entity';
import { PageState } from '../models/page-state';
import { CommonService } from './common.service';
import { RestService } from './rest.service';

@Injectable()
export class KendoGridService<
  T extends IdEntity
> extends BehaviorSubject<GridDataResult> {
  private restService = inject(RestService);
  private commonService = inject(CommonService);

  private dataResult: GridDataResult;

  constructor() {
    super(null);
  }
  public read$(
    url: string,
    state: PageState,
    microservice: boolean = false,
    queryParams: HttpParams = null,
    enabled: boolean = null
  ): Observable<GridDataResult> {
    let queryStr = this.commonService.toQueryString(state, microservice);
    if (microservice) {
      if (enabled) {
        queryStr = queryStr + '&enabled=' + enabled;
      }
      return this.restService
        .getHttp()
        .get<any>(`${url}?${queryStr}`, {
          observe: 'response',
          params: queryParams
        })
        .pipe(
          tap((response: any) => {
            this.dataResult = response;
            //--- workaround for mapping the fields from the new microservice to the expected fields for GridDataResult
            if (!this.dataResult.total) {
              const headers = response.headers;
              const totalCount = headers.get('totalcount');
              this.dataResult = {
                data: response.body,
                total: totalCount
              };
            }
            //---
            super.next(this.dataResult);
          }),
          catchError((error: HttpErrorResponse) =>
            this.commonService.handleError$(error)
          )
        );
    } else {
      return this.restService.get$<GridDataResult>(`${url}?${queryStr}`).pipe(
        tap((response: any) => {
          this.dataResult = response;
          super.next(this.dataResult);
        }),
        catchError((error: HttpErrorResponse) =>
          this.commonService.handleError$(error)
        )
      );
    }
  }

  public create$(url: string, dataItem: T): Observable<string> {
    return this.restService.post$<string>(url, dataItem).pipe(
      tap((response: any) => {
        if (response && response.id) {
          dataItem.id = response.id;
        }
        this.dataResult.data.unshift(dataItem);
        this.dataResult.total++;
        super.next(this.dataResult);
      }),
      catchError((error: HttpErrorResponse) =>
        this.commonService.handleError$(error)
      )
    );
  }

  public update$(
    url: string,
    dataItem: T,
    microservice: boolean = false
  ): Observable<string> {
    if (microservice) {
      return this.restService
        .put$<string>(`${url}${dataItem.id}`, dataItem)
        .pipe(
          tap(() => {
            const index = this.dataResult.data.findIndex(
              x => x.id === dataItem.id
            );
            Object.assign(this.dataResult.data[index], dataItem);
            super.next(this.dataResult);
          }),
          catchError((error: HttpErrorResponse) =>
            this.commonService.handleError$(error)
          )
        );
    } else {
      return this.restService.post$<string>(url, dataItem).pipe(
        tap(() => {
          const index = this.dataResult.data.findIndex(
            x => x.id === dataItem.id
          );
          Object.assign(this.dataResult.data[index], dataItem);
          super.next(this.dataResult);
        }),
        catchError((error: HttpErrorResponse) =>
          this.commonService.handleError$(error)
        )
      );
    }
  }

  public updateMicroservice$(url: string, dataItem: T): Observable<string> {
    return this.restService.put$<string>(url, dataItem).pipe(
      tap(() => {
        const index = this.dataResult.data.findIndex(x => x.id === dataItem.id);
        Object.assign(this.dataResult.data[index], dataItem);
        super.next(this.dataResult);
      }),
      catchError((error: HttpErrorResponse) =>
        this.commonService.handleError$(error)
      )
    );
  }

  public delete$(
    url: string,
    dataItem: T,
    microservice: boolean = false
  ): Observable<unknown> {
    const urlSuffix = microservice ? `${dataItem.id}` : `?id=${dataItem.id}`;
    return this.restService.delete$(url + urlSuffix).pipe(
      tap(() => {
        const index = this.dataResult.data.findIndex(x => x.id === dataItem.id);
        this.dataResult.data.splice(index, 1);
        this.dataResult.total--;
        super.next(this.dataResult);
      }),
      catchError((error: HttpErrorResponse) =>
        this.commonService.handleError$(error)
      )
    );
  }
}
